import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import {
  mlsPurchaseLinesIncludePasswords,
  mlsPurchaseOrderTypeLabel,
} from "../mls-purchases/orderTypeChip"
import AdminConciergeMagicLoginActions from "./AdminConciergeMagicLoginActions"

function formatWhen(value) {
  if (!value) return "—"
  try {
    return format(new Date(value), "MMM d, yyyy h:mm a")
  } catch {
    return String(value)
  }
}

function JsonBlock({ label, value }) {
  return (
    <Column gap={0.5}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="pre"
        variant="body2"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "action.hover",
          overflow: "auto",
          maxHeight: 280,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "monospace",
          fontSize: "0.8rem",
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Typography>
    </Column>
  )
}

export default function AdminMlsPurchaseDetailDialog({
  open,
  purchaseId,
  onClose,
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [purchase, setPurchase] = useState(null)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    if (!open || !purchaseId) {
      return
    }
    setLoading(true)
    setError("")
    setPurchase(null)
    adminApi
      .fetchMlsPurchaseDetail(purchaseId)
      .then(({ data }) => setPurchase(data))
      .catch(() => setError("Unable to load purchase details."))
      .finally(() => setLoading(false))
  }, [open, purchaseId])

  const unpaid =
    purchase?.status === "awaiting_payment" ||
    purchase?.status === "pending_checkout"

  function handleCancelUnpaid() {
    if (
      !window.confirm(
        "Cancel this unpaid order? Its payment link stops working."
      )
    ) {
      return
    }
    setCancelling(true)
    adminApi
      .cancelUnpaidMlsPurchase(purchase.id)
      .then(({ data }) => setPurchase(data))
      .catch(() => setError("Could not cancel this order."))
      .finally(() => setCancelling(false))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>MLS purchase</DialogTitle>
      <DialogContent>
        {loading ? (
          <Row sx={{ justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Row>
        ) : purchase ? (
          <Column gap={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2">
              <strong>Customer:</strong> {purchase.user_email} (user #
              {purchase.user_id})
            </Typography>
            <Typography variant="body2">
              <strong>Purchase ID:</strong> {purchase.id}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {mlsPurchaseOrderTypeLabel(purchase)}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {purchase.status} /{" "}
              {purchase.assignment_status}
              {purchase.error_message ? ` — ${purchase.error_message}` : ""}
            </Typography>
            {purchase.payment_link ? (
              <Row sx={{ alignItems: "center", gap: 1 }}>
                <Typography variant="body2">
                  <strong>Waiting for payment</strong>
                  {purchase.payer_email ? ` from ${purchase.payer_email}` : ""}.
                  Payment link:
                </Typography>
                <Row>
                  <AdminConciergeMagicLoginActions
                    loginLink={purchase.payment_link}
                    compact
                    openLabel="Open payment page"
                    copyLabel="Copy payment link (expires in 7 days)"
                    copyAriaLabel="Copy payment link"
                  />
                </Row>
              </Row>
            ) : null}
            {purchase.subscription_cancel_at_end && (
              <Typography variant="body2" sx={{ color: "warning.main" }}>
                <strong>Cancellation:</strong> Subscription set to cancel at
                period end
                {purchase.subscription_term_ends_at
                  ? ` — access ends ${formatWhen(
                      purchase.subscription_term_ends_at
                    )}`
                  : ""}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Paid:</strong> {formatWhen(purchase.paid_at)} ·{" "}
              <strong>Activated:</strong>{" "}
              {formatWhen(purchase.mls_activated_at)}
            </Typography>
            <Row gap={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="body2">
                <strong>Zoho Subscription ID:</strong>{" "}
                {purchase.zoho_subscription_id || "—"}
              </Typography>
            </Row>
            <Row gap={1} sx={{ alignItems: "center" }}>
              <Typography variant="body2">
                <strong>Invoice ID:</strong> {purchase.zoho_invoice_id || "—"}
              </Typography>
              {purchase.zoho_invoice_id ? (
                purchase.invoice_pdf_url ? (
                  <Tooltip title="View invoice PDF">
                    <IconButton
                      size="small"
                      component="a"
                      href={purchase.invoice_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View invoice PDF"
                    >
                      <ReceiptOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Invoice PDF not available">
                    <span>
                      <IconButton size="small" disabled>
                        <ReceiptOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              ) : null}
            </Row>
            <JsonBlock
              label="Contact details"
              value={purchase.contact_details || {}}
            />
            <JsonBlock
              label={
                mlsPurchaseLinesIncludePasswords(purchase)
                  ? "MLS lines (includes passwords — staff only)"
                  : "MLS lines"
              }
              value={purchase.lines || []}
            />
          </Column>
        ) : (
          <Alert severity="error">{error || "Purchase not found."}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {unpaid ? (
          <Button
            color="error"
            disabled={cancelling}
            onClick={handleCancelUnpaid}
          >
            Cancel unpaid order
          </Button>
        ) : null}
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
